import { BACKEND_URL } from '../config/backendUrl';
/**
 * Short-link resolver page.
 *
 * The email sends recipients to `/s/<code>` — a short, branded URL. This
 * component calls `/api/public/short/<code>`, gets the real target path,
 * and immediately navigates there. Keeps the emailed link clean (short,
 * no opaque 200-char JWT) so Google Safe Browsing / spam filters don't
 * flag the message.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';

const API = `${BACKEND_URL}/api`;

export default function ShortLinkPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/short/${code}`);
        if (cancelled) return;
        const target = res.data?.target;
        if (!target) {
          setError('This link has expired or is invalid.');
          return;
        }
        navigate(target, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || 'This link has expired or is invalid.';
        setError(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [code, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-slate-200 p-10 text-center" data-testid="short-link-page">
        {error ? (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Link unavailable</h1>
            <p className="text-sm text-slate-600 mb-6" data-testid="short-link-error">{error}</p>
            <p className="text-xs text-slate-500">
              If you believe this is an error, please contact the person who sent you this link.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-600">Preparing your secure document…</p>
          </>
        )}
      </div>
    </div>
  );
}
